/**
 * PERSON RELATION CARD — card personalizzata per RelationPanel quando
 * il targetType e' Person. Contenuto: nome+cognome, nomi di tutte le
 * aziende collegate (comma-separated), email primaria con copy,
 * cellulare con copy, telefono (primo non-primary), descrizione relazione.
 * La stellina, quando il pannello la espone, marca il contatto preferito
 * dell'azienda (FILO-25): accade solo nel box Contatti della scheda azienda.
 *
 * Fa 2 fetch al mount:
 *   - personApi.getById → emails + phones
 *   - relationshipApi.getByEntityBidirectional con source=Person →
 *     nomi delle aziende collegate
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { MailGlyph, PhoneGlyph } from './icons';
import { personApi } from '../entities/person/personApi';
import { relationshipApi } from '../entities/relationship/relationshipApi';
import { useCurrentGroupId } from '../auth/authSelectors';
import { useToast } from './useToast';
import {
  getEntityFqn,
  getEntityShortNameByFqn,
  resolveEntityLabel,
} from '../entityRegistry/entityRegistry';
import type { Person } from '../entities/person/personTypes';
import type { RelationItemContext } from './RelationPanel';

interface PersonRelationCardProps {
  ctx: RelationItemContext;
  /** personId = ctx.relationship.classPK2 (lato opposto alla source). */
  personId: number;
}

export function PersonRelationCard({ ctx, personId }: PersonRelationCardProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const groupId = useCurrentGroupId();
  const [person, setPerson] = useState<Person | null>(null);
  const [companies, setCompanies] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    personApi
      .getById(personId)
      .then((p) => { if (!cancelled) setPerson(p); })
      .catch(() => { if (!cancelled) setPerson(null); });
    return () => { cancelled = true; };
  }, [personId]);

  // Fetch relazioni del Person e filtra solo quelle verso Business.
  // Le label vengono risolte una per una (la lista e' tipicamente piccola).
  useEffect(() => {
    let cancelled = false;
    const personFqn = getEntityFqn('Person');
    const businessFqn = getEntityFqn('Business');
    if (!personFqn || !businessFqn || !groupId) return;
    (async () => {
      try {
        const rels = await relationshipApi.getByEntityBidirectional(groupId, personFqn, personId);
        const businessRels = rels.filter((r) => r.className2 === businessFqn);
        const names = await Promise.all(
          businessRels.map(async (r) => {
            const short = getEntityShortNameByFqn(r.className2) ?? 'Business';
            return resolveEntityLabel(short, r.classPK2);
          }),
        );
        if (!cancelled) setCompanies(names.filter(Boolean));
      } catch {
        if (!cancelled) setCompanies([]);
      }
    })();
    return () => { cancelled = true; };
  }, [personId, groupId]);

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ severity: 'success', message: t('common.copied') });
    } catch {
      toast({ severity: 'error', message: t('common.copyFailed') });
    }
  }

  const primaryEmail = person?.emailAddresses?.find((e) => e.primary)?.address
    ?? person?.emailAddresses?.[0]?.address
    ?? null;
  // Convenzione senza type field: primary = cellulare, non-primary = telefono.
  const mobile = person?.phones?.find((p) => p.primary)?.number ?? null;
  const landline = person?.phones?.find((p) => !p.primary)?.number ?? null;

  const fullName = person
    ? `${person.firstName} ${person.lastName}`.trim()
    : ctx.label;
  const emblem = fullName.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '—';

  return (
    <div className="relation-card relation-card--person">
      {/* Testata: emblema iniziali + nome (con aziende sotto) + stella + rimuovi,
          su una riga (stile mockup .rel-person-head). */}
      <div className="relation-card__phead">
        <span className="rel-emblem">{emblem}</span>
        <div className="relation-card__main">
          <Link to={ctx.path} className="relation-link relation-card__title">{fullName}</Link>
          {/* Ruolo/descrizione della relazione (es. "Cliente") sotto al nome. */}
          {(ctx.relationship.reverseDescription || ctx.relationship.description) && (
            <span className="relation-card__role">
              {ctx.relationship.reverseDescription || ctx.relationship.description}
            </span>
          )}
          {companies.length > 0 && (
            <span className="relation-card__company">{companies.join(', ')}</span>
          )}
        </div>
        {ctx.requestSetPrimary && (
          <IconButton
            className={`rel-star${ctx.isPrimary ? ' rel-star--on' : ''}`}
            size="small"
            onClick={() => ctx.requestSetPrimary!(!ctx.isPrimary)}
            aria-label={ctx.isPrimary ? t('relationship.unsetPreferredContact') : t('relationship.setPreferredContact')}
            title={ctx.isPrimary ? t('relationship.unsetPreferredContact') : t('relationship.setPreferredContact')}
          >
            {ctx.isPrimary ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        )}
        {ctx.requestEdit && (
          <IconButton
            size="small"
            onClick={ctx.requestEdit}
            aria-label={t('relationship.editDescriptions')}
            title={t('relationship.editDescriptions')}
            className="relation-card__edit"
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton
          size="small"
          onClick={ctx.requestDelete}
          aria-label={t('relationship.removeLabel')}
          className="relation-card__remove"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      {/* Righe di contatto (email/telefoni) con icona e copia. */}
      {(primaryEmail || mobile || landline) && (
        <div className="relation-card__contacts">
          {primaryEmail && (
            <div className="cline">
              <MailGlyph />
              <span className="cval">{primaryEmail}</span>
              <IconButton size="small" onClick={() => copy(primaryEmail)} aria-label={t('common.copy')} className="cline-copy">
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </div>
          )}
          {mobile && (
            <div className="cline">
              <PhoneGlyph />
              <span className="cval tel">{mobile}</span>
              <IconButton size="small" onClick={() => copy(mobile)} aria-label={t('common.copy')} className="cline-copy">
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </div>
          )}
          {landline && (
            <div className="cline">
              <PhoneGlyph />
              <span className="cval tel">{landline}</span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
